import {
  createGHLSession,
  createJotformSession,
  deleteCognitoSession,
  deleteGHLSession,
  deleteJotformSession,
  deleteSuiteDashSession,
  getCognitoSession,
  getGHLSession,
  getJotformSession,
  getSuiteDashSession,
} from "@components/Integrations/api";
import {
  Button,
  Chip,
  Container,
  Group,
  Paper,
  Stack,
  Title,
  Tooltip,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { IntegrationSession } from "@components/Integrations/types";
import { emitDebugEvent } from "@utils/debug/eventIntegration";
import { openNewTab } from "@/utils/browserUtils";

const Jotform = (window as any).JF;

const QueryParams = {
  openJotform: "jotform",
  ghlCode: "code",
  integrationAttemptId: "integrationAttemptId",
};

// Must stay in sync with the extension's `IntegrationLinks.GHL.marketplaceApp()`
// and the backend `GHL_CLIENT_ID`/`GHL_CLIENT_SECRET` — all three must reference
// the SAME GHL marketplace app. The backend exchanges the returned `code` with its
// configured client credentials, so a code minted for a different app (or without
// the scopes `get_session()` probes, e.g. locations/customFields.readonly) either
// fails the token exchange or gets its session deleted by the permission check.
const GHLMarketplaceUrl =
  "https://marketplace.gohighlevel.com/v2/oauth/chooselocation" +
  "?response_type=code" +
  "&redirect_uri=https%3A%2F%2Fapp.smartwiz.io%2Fhome%2Flite%3Fsetting%3Dintegrations" +
  "&client_id=6904e6cdb48d9c96ef874f44-mjrhc6p2" +
  "&scope=oauth.readonly+forms.readonly+surveys.readonly" +
  "+locations%2FcustomFields.readonly+locations%2FcustomValues.readonly" +
  "+contacts.readonly+medias.readonly" +
  "&version_id=6a34582a5a829d60faf383f9";

/**
 * Displays the Integrations login interface.
 * @constructor
 */
export default function IntegrationSettings() {
  const [searchParams] = useSearchParams();
  const [jotformSession, setJotformSession] =
    useState<IntegrationSession | null>(null);
  const [cognitoSession, setCognitoSession] =
    useState<IntegrationSession | null>(null);
  const [suiteDashSession, setSuiteDashSession] =
    useState<IntegrationSession | null>(null);
  const [ghlSession, setGhlSession] = useState<IntegrationSession | null>(null);

  // Get integration attempt ID from URL query parameters
  const integrationAttemptId = searchParams.get(
    QueryParams.integrationAttemptId
  );

  /**
   * Creates a new Jotform session using the provided API key.
   * @param apiKey - The Jotform API key.
   */
  function handleCreateJotformSession(apiKey: string) {
    // Removed INTEGRATION_CONNECTION_STARTED/COMPLETED - service layer handles event emission
    // UI handlers should focus on UI state, not debug event tracking

    createJotformSession(apiKey, integrationAttemptId || undefined)
      .then((newSession) => {
        setJotformSession(newSession);

        window.postMessage(
          {
            type: "JOTFORM_LOGIN_SUCCESS",
            source: "smartwiz_crm",
          },
          window.location.origin
        );
      })
      .catch((error) => {
        // Emit debug event for connection failure
        emitDebugEvent("error", "INTEGRATION_CONNECTION_FAILED", "web", {
          integrationName: "Jotform",
          errorMessage:
            error instanceof Error
              ? error.message.substring(0, 200)
              : "Connection failed",
        });
        console.log("Error creating Jotform session.");
      });
  }

  /**
   * Creates a new GHL session using the provided API key.
   * @param code - The GHL access code.
   */
  function handleCreateGHLSession(code: string) {
    // Removed INTEGRATION_CONNECTION_STARTED/COMPLETED - service layer handles event emission
    // UI handlers should focus on UI state, not debug event tracking

    createGHLSession(code)
      .then((newSession) => {
        setGhlSession(newSession);

        window.postMessage(
          {
            type: "GHL_LOGIN_SUCCESS",
            source: "smartwiz_crm",
          },
          window.location.origin
        );
      })
      .catch((error) => {
        // Emit debug event for connection failure
        emitDebugEvent("error", "INTEGRATION_CONNECTION_FAILED", "web", {
          integrationName: "Go High Level",
          errorMessage:
            error instanceof Error
              ? error.message.substring(0, 200)
              : "Connection failed",
        });
        console.log("Error creating GHL session.");
      });
  }

  /**
   * Opens the Integrations login popup.
   */
  function handleOpenJotform() {
    // Removed INTEGRATION_CONNECTION_STARTED - service layer handles event emission
    // Events should be emitted when connection actually starts, not on click

    Jotform.login(
      () => {
        handleCreateJotformSession(Jotform.getAPIKey());
        console.log("Jotform login successful");
      },
      () => {
        // Emit debug event for connection failure
        emitDebugEvent("error", "INTEGRATION_CONNECTION_FAILED", "web", {
          integrationName: "Jotform",
          errorMessage: "Login popup error",
        });
        console.log("Jotform login error");
      }
    );

    // Set the popup to be a larger size
    const popup = document.getElementById("jotform_oauth_frame");
    if (popup) popup.style.height = "80vh";
  }

  /**
   * Opens the GHL marketplace in a new tab.
   */
  function handleOpenGHL() {
    // Removed INTEGRATION_CONNECTION_STARTED - service layer handles event emission
    // Events should be emitted when connection actually starts, not on click
    openNewTab(GHLMarketplaceUrl);
  }

  function handleDeleteJotformSession() {
    deleteJotformSession()
      .then(() => {
        setJotformSession(null);
        // Emit debug event for disconnection
        emitDebugEvent("action", "INTEGRATION_DISCONNECTED", "web", {
          integrationName: "Jotform",
        });
      })
      .catch((error) => {
        emitDebugEvent("error", "INTEGRATION_CONNECTION_FAILED", "web", {
          integrationName: "Jotform",
          errorMessage:
            error instanceof Error
              ? error.message.substring(0, 200)
              : "Disconnect failed",
        });
      });
  }

  function handleDeleteCognitoSession() {
    deleteCognitoSession()
      .then(() => {
        setCognitoSession(null);
        // Emit debug event for disconnection
        emitDebugEvent("action", "INTEGRATION_DISCONNECTED", "web", {
          integrationName: "Cognito Forms",
        });
      })
      .catch((error) => {
        emitDebugEvent("error", "INTEGRATION_CONNECTION_FAILED", "web", {
          integrationName: "Cognito Forms",
          errorMessage:
            error instanceof Error
              ? error.message.substring(0, 200)
              : "Disconnect failed",
        });
      });
  }

  function handleDeleteGHLSession() {
    deleteGHLSession()
      .then(() => {
        setGhlSession(null);
        // Emit debug event for disconnection
        emitDebugEvent("action", "INTEGRATION_DISCONNECTED", "web", {
          integrationName: "Go High Level",
        });
      })
      .catch((error) => {
        emitDebugEvent("error", "INTEGRATION_CONNECTION_FAILED", "web", {
          integrationName: "Go High Level",
          errorMessage:
            error instanceof Error
              ? error.message.substring(0, 200)
              : "Disconnect failed",
        });
      });
  }

  function handleDeleteSuiteDashSession() {
    deleteSuiteDashSession()
      .then(() => {
        setSuiteDashSession(null);
        // Emit debug event for disconnection
        emitDebugEvent("action", "INTEGRATION_DISCONNECTED", "web", {
          integrationName: "SuiteDash",
        });
      })
      .catch((error) => {
        emitDebugEvent("error", "INTEGRATION_CONNECTION_FAILED", "web", {
          integrationName: "SuiteDash",
          errorMessage:
            error instanceof Error
              ? error.message.substring(0, 200)
              : "Disconnect failed",
        });
      });
  }

  /**
   * On load, check if there's an existing Integrations session.
   */
  useEffect(() => {
    const doOpenJotform = searchParams.get(QueryParams.openJotform) === "true";
    const ghlCode = searchParams.get(QueryParams.ghlCode);

    // If there's a Jotform flag in the URL, open the Jotform login popup
    if (doOpenJotform) {
      handleOpenJotform();
    } else {
      getJotformSession().then(setJotformSession);
    }

    // If there's a GHL code in the URL, create the GHL session
    if (ghlCode) {
      handleCreateGHLSession(ghlCode);
    } else {
      getGHLSession().then(setGhlSession);
    }

    getCognitoSession().then(setCognitoSession);
    getSuiteDashSession().then(setSuiteDashSession);
  }, []);

  return (
    <Container size="sm" mx="auto" my="xl">
      <Paper variant="outlined" p="md" withBorder mb="md">
        <Group justify="space-between" align="center">
          <Stack>
            <Title order={3}>Jotform Integration</Title>
            {jotformSession && (
              <Chip size="lg" checked={false}>
                {jotformSession.email}
              </Chip>
            )}
          </Stack>
          {jotformSession ? (
            <Button
              color="red"
              variant="outline"
              onClick={handleDeleteJotformSession}
            >
              Disconnect
            </Button>
          ) : (
            <Button onClick={handleOpenJotform}>Connect</Button>
          )}
        </Group>
      </Paper>
      <Paper variant="outlined" p="md" withBorder mb="md">
        <Group justify="space-between" align="center">
          <Stack>
            <Title order={3}>Cognito Forms Integration</Title>
            {cognitoSession && (
              <Chip size="lg" checked={false}>
                {cognitoSession.email}
              </Chip>
            )}
          </Stack>
          {cognitoSession ? (
            <Button
              color="red"
              variant="outline"
              onClick={handleDeleteCognitoSession}
            >
              Disconnect
            </Button>
          ) : (
            <Tooltip label="Open your Wizzy extension settings to login to Cognito Forms">
              <Button disabled>Connect</Button>
            </Tooltip>
          )}
        </Group>
      </Paper>
      <Paper variant="outlined" p="md" withBorder mb="md">
        <Group justify="space-between" align="center">
          <Stack>
            <Title order={3}>Go High Level Integration</Title>
            {ghlSession && (
              <Chip size="lg" checked={false}>
                Connected
              </Chip>
            )}
          </Stack>
          {ghlSession ? (
            <Button
              color="red"
              variant="outline"
              onClick={handleDeleteGHLSession}
            >
              Disconnect
            </Button>
          ) : (
            <Button onClick={handleOpenGHL}>Connect</Button>
          )}
        </Group>
      </Paper>
      <Paper variant="outlined" p="md" withBorder mb="md">
        <Group justify="space-between" align="center">
          <Stack>
            <Title order={3}>SuiteDash Integration</Title>
            {suiteDashSession && (
              <Chip size="lg" checked={false}>
                {suiteDashSession.email}
              </Chip>
            )}
          </Stack>
          {suiteDashSession ? (
            <Button
              color="red"
              variant="outline"
              onClick={handleDeleteSuiteDashSession}
            >
              Disconnect
            </Button>
          ) : (
            <Tooltip label="Open your Wizzy extension settings to login to SuiteDash">
              <Button disabled>Connect</Button>
            </Tooltip>
          )}
        </Group>
      </Paper>
    </Container>
  );
}
